import * as Cesium from 'cesium';
import type { Scene } from 'cesium';

// `Pass` is exported at runtime but missing from the public typings; 1 is its COMPUTE value
// up to at least 1.146. See docs/cesium-upgrade-checklist.md.
const COMPUTE_PASS = (Cesium as unknown as { Pass?: { COMPUTE?: number } }).Pass?.COMPUTE ?? 1;

interface QueuedCommand {
	pass: number;
}

let warnedAboutInternals = false;

/**
 * Works around a CesiumJS bug (present up to at least 1.146) that stops rendering with
 * "DeveloperError: This object was destroyed" in `Imagery.releaseReference`.
 *
 * With `scene.pickTranslucentDepth` on, every `pickPosition` (our drawing tools on mouse
 * move, the camera controller on every drag and zoom) re-renders the scene from the last
 * frame's `frameState.commandList` without resetting it, so that frame's compute commands
 * run again. Globe imagery reprojection is one of them: each re-run releases its imagery
 * once more, and a later tile unload releases an imagery that is already destroyed.
 *
 * A compute command has done its work once the frame that queued it is rendered, so this
 * drops them from the list right after each render. Returns the listener remover.
 */
export function dropComputeCommandsAfterRender(scene: Scene): () => void {
	return scene.postRender.addEventListener(() => {
		// `frameState` is private API; Cesium keeps this array, so edit it in place.
		const commandList = (scene as unknown as { frameState?: { commandList?: unknown } }).frameState?.commandList;
		if (!Array.isArray(commandList)) {
			// A Cesium upgrade changed the internals: skip rather than break rendering.
			if (!warnedAboutInternals) {
				warnedAboutInternals = true;
				console.warn('dropComputeCommandsAfterRender: frameState.commandList not found, workaround skipped');
			}
			return;
		}
		const commands = commandList as QueuedCommand[];
		let kept = 0;
		for (const command of commands) {
			if (command.pass !== COMPUTE_PASS) commands[kept++] = command;
		}
		commands.length = kept;
	});
}
